import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import API from '../lib/api';
import { Brain, Zap, Target, TrendingUp, FileText, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip
} from 'recharts';
import EnginePredictionBanner from '../components/dashboard/EnginePredictionBanner';

export default function AIMatchLab() {
    const [searchParams] = useSearchParams();
    const [fixtures, setFixtures] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState(searchParams.get('match') || '');
    const [prediction, setPrediction] = useState(null);
    const [report, setReport] = useState('');
    const [loading, setLoading] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);

    useEffect(() => {
        API.get('/fixtures').then(res => setFixtures(res.data.fixtures || []));
    }, []);

    useEffect(() => {
        if (selectedMatch) {
            setLoading(true);
            setPrediction(null);
            setReport('');
            API.get(`/prediction/${selectedMatch}`).then(res => {
                setPrediction(res.data);
                setLoading(false);
            }).catch(() => setLoading(false));
        }
    }, [selectedMatch]);

    const generateReport = async () => {
        setReportLoading(true);
        try {
            const res = await API.post(`/prediction/${selectedMatch}/report`);
            setReport(res.data.report);
        } catch (err) {
            console.error(err);
        }
        setReportLoading(false);
    };

    const winPieData = prediction ? [
        { name: prediction.team_a?.short_name, value: prediction.team_a?.win_probability, fill: '#00E5FF' },
        { name: prediction.team_b?.short_name, value: prediction.team_b?.win_probability, fill: '#FF2D95' },
    ] : [];

    return (
        <div className="space-y-6" data-testid="ai-match-lab">
            <EnginePredictionBanner engineKey="elo_rating" matchId={selectedMatch} />
            <div className="glass-card p-6">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <Brain className="w-5 h-5 text-purple-400" />
                    <h2 className="heading-primary text-base md:text-xl text-white">SELECT MATCH FOR AI ANALYSIS</h2>
                    <div className="badge-ai">8 AI ENGINES</div>
                </div>
                <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                    <SelectTrigger data-testid="match-selector" className="bg-black/50 border-white/10 text-white h-12 max-w-lg">
                        <SelectValue placeholder="Choose a match..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                        {fixtures.map(f => (
                            <SelectItem key={f.id} value={f.id} className="text-white hover:bg-white/10">
                                {f.home_team?.short_name} vs {f.away_team?.short_name} - {f.venue?.city} ({f.status})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {loading && (
                <div className="flex items-center justify-center h-48">
                    <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                </div>
            )}

            {prediction && !loading && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-5 glass-card p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Target className="w-4 h-4 text-cyan-400" />
                            <h3 className="heading-primary text-sm text-white">WIN PROBABILITY</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={winPieData} cx="50%" cy="50%" innerRadius={65} outerRadius={95} paddingAngle={4} dataKey="value">
                                    {winPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="flex justify-between mt-4">
                            <div className="text-center">
                                <div className="heading-primary text-3xl neon-text">{prediction.team_a?.win_probability}%</div>
                                <div className="tech-mono text-xs text-white/40">{prediction.team_a?.short_name}</div>
                            </div>
                            <div className="text-center">
                                <div className="heading-primary text-3xl neon-text-pink">{prediction.team_b?.win_probability}%</div>
                                <div className="tech-mono text-xs text-white/40">{prediction.team_b?.short_name}</div>
                            </div>
                        </div>
                        <div className="mt-4 text-center">
                            <div className="tech-mono text-[10px] text-white/30">CONFIDENCE</div>
                            <div className="heading-primary text-2xl text-yellow-400">{(prediction.confidence * 100).toFixed(0)}%</div>
                        </div>
                    </div>

                    <div className="lg:col-span-7 space-y-4">
                        <div className="glass-card p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Zap className="w-4 h-4 text-yellow-400" />
                                <h3 className="heading-primary text-sm text-white">TEAM STRENGTH COMPARISON</h3>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <div className="tech-mono text-xs text-cyan-400 mb-2">{prediction.team_a?.short_name}</div>
                                    <div className="heading-primary text-4xl text-white">{prediction.team_a?.strength_score}</div>
                                    <div className="tech-mono text-[10px] text-white/30">STRENGTH INDEX</div>
                                    <div className="mt-4 space-y-2">
                                        {prediction.team_a?.key_players?.map((p, i) => (
                                            <div key={i} className="flex justify-between items-center">
                                                <span className="text-sm text-white/80">{p.name}</span>
                                                <span className="text-xs neon-text font-bold">{p.impact}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <div className="tech-mono text-xs text-pink-400 mb-2">{prediction.team_b?.short_name}</div>
                                    <div className="heading-primary text-4xl text-white">{prediction.team_b?.strength_score}</div>
                                    <div className="tech-mono text-[10px] text-white/30">STRENGTH INDEX</div>
                                    <div className="mt-4 space-y-2">
                                        {prediction.team_b?.key_players?.map((p, i) => (
                                            <div key={i} className="flex justify-between items-center">
                                                <span className="text-sm text-white/80">{p.name}</span>
                                                <span className="text-xs neon-text-pink font-bold">{p.impact}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="glass-card p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <TrendingUp className="w-4 h-4 text-green-400" />
                                <h3 className="heading-primary text-sm text-white">VENUE INTELLIGENCE</h3>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="text-center p-3 bg-white/5 rounded-lg">
                                    <div className="heading-primary text-2xl text-white">{prediction.venue_analysis?.avg_first_innings_score}</div>
                                    <div className="tech-mono text-[10px] text-white/40">AVG 1ST INNINGS</div>
                                </div>
                                <div className="text-center p-3 bg-white/5 rounded-lg">
                                    <div className="heading-primary text-2xl text-white">{prediction.venue_analysis?.chase_success_rate}%</div>
                                    <div className="tech-mono text-[10px] text-white/40">CHASE SUCCESS</div>
                                </div>
                                <div className="text-center p-3 bg-white/5 rounded-lg">
                                    <div className="heading-primary text-2xl text-white">{prediction.venue_analysis?.boundary_frequency}</div>
                                    <div className="tech-mono text-[10px] text-white/40">BOUNDARY FREQ</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-12 glass-card p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Zap className="w-4 h-4 text-purple-400" />
                            <h3 className="heading-primary text-sm text-white">MONTE CARLO SIMULATION</h3>
                            <span className="tech-mono text-[10px] text-white/30">{prediction.simulation?.simulations?.toLocaleString()} RUNS</span>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={prediction.simulation?.distribution || []}>
                                <XAxis dataKey="range" tick={{ fill: '#666', fontSize: 10 }} interval={1} />
                                <YAxis hide />
                                <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }} />
                                <Bar dataKey="team_a" fill="#00E5FF" opacity={0.7} name={prediction.team_a?.short_name} />
                                <Bar dataKey="team_b" fill="#FF2D95" opacity={0.7} name={prediction.team_b?.short_name} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* H2H & Insights */}
                    {(prediction.insights?.length > 0 || prediction.h2h_data) && (
                        <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {prediction.h2h_data?.total_matches > 0 && (
                                <div className="glass-card p-6" data-testid="h2h-section">
                                    <h3 className="heading-primary text-sm text-white mb-4">HEAD-TO-HEAD RECORD</h3>
                                    <div className="flex items-center justify-between">
                                        <div className="text-center flex-1">
                                            <div className="heading-primary text-3xl text-cyan-400">{prediction.h2h_data.team_a?.wins}</div>
                                            <div className="tech-mono text-xs text-white/40">{prediction.team_a?.short_name} WINS</div>
                                        </div>
                                        <div className="text-center px-4">
                                            <div className="heading-primary text-lg text-white/30">{prediction.h2h_data.total_matches} matches</div>
                                        </div>
                                        <div className="text-center flex-1">
                                            <div className="heading-primary text-3xl text-pink-400">{prediction.h2h_data.team_b?.wins}</div>
                                            <div className="tech-mono text-xs text-white/40">{prediction.team_b?.short_name} WINS</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {prediction.insights?.length > 0 && (
                                <div className="glass-card p-6" data-testid="insights-section">
                                    <h3 className="heading-primary text-sm text-white mb-4">AI INSIGHTS</h3>
                                    <div className="space-y-3">
                                        {prediction.insights.map((insight, i) => (
                                            <div key={i} className="flex items-start gap-2 p-2 bg-white/5 rounded-lg">
                                                <TrendingUp className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
                                                <span className="text-sm text-white/80">{insight}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="lg:col-span-12 glass-card p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-cyan-400" />
                                <h3 className="heading-primary text-sm text-white">AI PREDICTION REPORT</h3>
                                <div className="badge-ai">GPT-5.2</div>
                            </div>
                            <Button data-testid="generate-report-btn" onClick={generateReport} disabled={reportLoading}
                                className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30">
                                {reportLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : 'Generate AI Report'}
                            </Button>
                        </div>
                        {report ? (
                            <div className="bg-black/50 rounded-lg p-6 whitespace-pre-wrap text-sm text-white/80 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {report}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-white/30 tech-mono text-sm">
                                Click "Generate AI Report" for GPT-5.2 powered analysis
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-12">
                        <div className="flex flex-wrap gap-2">
                            {prediction.engines_used?.map((engine, i) => (
                                <span key={i} className="badge-ai">{engine}</span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
